/* eslint-disable qwik/no-use-visible-task */
import { FancyImage } from "@/common-components/fancy-image/FancyImage";
import { cn } from "@/utils/tailwind-helper";
import { $, component$, useSignal, useStore } from "@builder.io/qwik";
import { BREAKPOINTS } from "~/constants/mediaquery";
import type { IGenAsset, IGenHeroSliderFullHeight, IGenVideo } from "../../services/graphql/__generated/sdk";
import { HeroSliderFullHeightInner } from "./HeroSliderFullHeightInner";
import EmbededVideo from "../../common-components/embeded-video/EmbededVideo";

export interface HeroSliderFullHeightProps extends IGenHeroSliderFullHeight {
    componentIndex: number;
    nextComponentId?: string;
}

const pathShapes = [
    "M23.3,-42.4C34.1,-34.2,49.2,-35.7,56.2,-30.2C63.1,-24.7,62,-12.4,63.6,0.9C65.1,14.2,69.4,28.4,66.2,40.4C63,52.4,52.3,62.3,40.1,61.6C27.8,60.9,13.9,49.7,0.1,49.5C-13.7,49.4,-27.5,60.4,-37.7,59.9C-47.8,59.3,-54.5,47.3,-64.2,35.4C-73.9,23.5,-86.6,11.8,-85.6,0.6C-84.6,-10.6,-69.8,-21.2,-59.7,-32.4C-49.6,-43.6,-44.3,-55.5,-35.1,-64.5C-25.9,-73.6,-13,-79.9,-3.3,-74.1C6.3,-68.3,12.6,-50.6,23.3,-42.4Z",
    "M46,-70.1C60.2,-71.5,72.7,-60.3,79.1,-46.6C85.4,-32.8,85.7,-16.4,82.7,-1.7C79.7,12.9,73.4,25.9,67,39.5C60.5,53.1,53.8,67.4,42.6,74.3C31.4,81.3,15.7,80.9,0.9,79.3C-13.9,77.6,-27.7,74.8,-35.1,65.7C-42.6,56.6,-43.6,41.2,-49.7,29.2C-55.9,17.2,-67.2,8.6,-71.8,-2.6C-76.3,-13.8,-74,-27.6,-66.3,-36.9C-58.6,-46.2,-45.4,-51,-33.4,-50.9C-21.5,-50.8,-10.7,-45.8,2.6,-50.3C15.9,-54.7,31.8,-68.7,46,-70.1Z",
    "M29.6,-55.4C38,-46.4,44.2,-37.7,48.3,-28.6C52.4,-19.4,54.5,-9.7,53.6,-0.5C52.8,8.7,49,17.4,46.5,29.4C44.1,41.5,42.8,56.7,35.3,65.7C27.7,74.7,13.9,77.4,2.5,73.1C-8.9,68.8,-17.8,57.5,-25.1,48.4C-32.4,39.3,-38.2,32.3,-43.4,24.6C-48.6,16.9,-53.3,8.5,-58.5,-3C-63.8,-14.5,-69.6,-29,-65.1,-38C-60.6,-46.9,-45.8,-50.4,-33.3,-57C-20.7,-63.6,-10.3,-73.4,0.1,-73.7C10.6,-73.9,21.2,-64.4,29.6,-55.4Z",
    "M22.7,-37.5C33.3,-33.2,48.4,-34.9,56.4,-29.7C64.4,-24.5,65.2,-12.2,66.6,0.8C68,13.9,70,27.7,63.3,35.1C56.6,42.5,41.1,43.4,29.1,45.2C17,47,8.5,49.7,1.9,46.4C-4.7,43.1,-9.4,33.7,-19.9,31.1C-30.4,28.4,-46.7,32.4,-54.1,28.3C-61.5,24.2,-59.9,12.1,-58.7,0.7C-57.5,-10.7,-56.6,-21.5,-50.4,-27.6C-44.2,-33.7,-32.7,-35.2,-23.4,-40.3C-14.1,-45.4,-7.1,-54.2,-0.5,-53.3C6,-52.4,12.1,-41.9,22.7,-37.5Z",
];

// const generateDynamicFluidPathAnimation = (componentIndex: number) => {
//     let styles = "";
//     let frames = "";

//     for (let i = 0; i < pathShapes.length + 1; i++) {
//         frames += `
//             ${(100 / pathShapes.length) * i}% {
//                 d: path("${i === pathShapes.length ? pathShapes[0] : pathShapes[i]}");
//             }
//         `;
//     }

//     styles = `
//         @keyframes fluidPathAnimation {
//             ${frames}
//         }

//         #blobClip-${componentIndex} path {
//             animation: fluidPathAnimation 8s linear infinite;
//         }
//     `;

//     return styles;
// };

export const INITIAL_CLIP_PATH_COORDS = { x: 0, y: 0, scale: 0 };

export const HeroSliderFullHeight = component$<HeroSliderFullHeightProps>(
    ({ componentIndex, sliderItems, topDarkening, ...props }) => {
        const mediaSlides = sliderItems
            ?.filter((item) => item?.__typename === "Asset" || item?.__typename === "Video")
            .filter(Boolean) as (IGenAsset | IGenVideo)[];
        const TRANSITION_SPEED = 1000;
        const clipPathCoords = useStore(INITIAL_CLIP_PATH_COORDS);
        const activeSlideIndex = useSignal(0);
        const backgroundMediaSlide = useSignal<IGenAsset | IGenVideo>(mediaSlides[0]);
        const transitionState = useSignal<"IDLE" | "TRANSITIONING" | "DONE">("IDLE");
        const isAnimating = useSignal(false);
        const heroSliderRef = useSignal<HTMLDivElement>(null!);
        // const dynamicFluidPathAnimation = generateDynamicFluidPathAnimation(componentIndex);

        //  fill factor 100 = fill the whole screen 0 = no fill
        const getResponsiveScale = $(async (targetSize: number = 0, event?: MouseEvent) => {
            const heroSlider = heroSliderRef.value;
            if (!heroSlider) return 0;

            // Get viewport dimensions
            const viewportWidth = heroSlider.clientWidth;
            const viewportHeight = heroSlider.clientHeight;

            // Calculate base scale differently based on whether event is provided
            if (event) {
                // Event-based calculation (from mouse position)
                const { left, top } = heroSlider.getBoundingClientRect();
                const clickX = event.clientX - left;
                const clickY = event.clientY - top;

                const distances = [
                    Math.hypot(clickX, clickY),
                    Math.hypot(viewportWidth - clickX, clickY),
                    Math.hypot(clickX, viewportHeight - clickY),
                    Math.hypot(viewportWidth - clickX, viewportHeight - clickY),
                ];

                const maxDistance = Math.max(...distances);
                const baseScale = (maxDistance / 50) * 1.2;
                return baseScale * (targetSize / 100);
            } else {
                // Center-based calculation
                const centerX = viewportWidth / 2;
                const centerY = viewportHeight / 2;

                const distances = [
                    Math.hypot(centerX, centerY),
                    Math.hypot(viewportWidth - centerX, centerY),
                    Math.hypot(centerX, viewportHeight - centerY),
                    Math.hypot(viewportWidth - centerX, viewportHeight - centerY),
                ];

                const maxDistance = Math.max(...distances);
                const baseScale = (maxDistance / 50) * 1.2;
                return baseScale * (targetSize / 100);
            }
        });

        // 2. Modify animateClipPath to handle the new scale range
        const animateClipPathScale = $(async (targetSize = 0, event?: MouseEvent, duration = 300) => {
            const heroSlider = heroSliderRef.value;
            if (!heroSlider) return;

            // Calculate start and end scales
            const activeScale = clipPathCoords.scale;
            const targetScale = await getResponsiveScale(targetSize, event);

            let startTime: number;
            let animationFrameId: number;
            isAnimating.value = true;

            const animate = (timestamp: number) => {
                if (!startTime) startTime = timestamp;
                const elapsedTime = timestamp - startTime;
                const progress = Math.min(elapsedTime / duration, 1);

                // Interpolate between current and target scale
                const currentRadius = activeScale + (targetScale - activeScale) * progress;
                clipPathCoords.scale = currentRadius;

                if (progress < 1) {
                    animationFrameId = requestAnimationFrame(animate);
                } else {
                    clipPathCoords.scale = targetScale;
                    isAnimating.value = false;
                    cancelAnimationFrame(animationFrameId);
                }
            };

            animationFrameId = requestAnimationFrame(animate);
        });

        const getPrevSlide = $(() => {
            if (activeSlideIndex.value === 0) {
                return mediaSlides!.length - 1;
            } else {
                return activeSlideIndex.value - 1;
            }
        });

        const getNextSlide = $(() => {
            if (activeSlideIndex.value === mediaSlides!.length - 1) {
                return 0;
            } else {
                return activeSlideIndex.value + 1;
            }
        });

        const handleSlideAnimation = $(async (activeIndex: number, e?: MouseEvent, centeredTransition?: boolean) => {
            if (mediaSlides?.length === 1) return;

            transitionState.value = "TRANSITIONING";

            if (centeredTransition) {
                clipPathCoords.x = heroSliderRef.value!.clientWidth / 2;
                clipPathCoords.y = heroSliderRef.value!.clientHeight / 2;
            }

            await animateClipPathScale(100, e, 0); // Full screen

            let animationFrameId: number;
            requestAnimationFrame(() => {
                let startTime: number;
                const step = (timestamp: number) => {
                    if (!startTime) startTime = timestamp;
                    const elapsed = timestamp - startTime;
                    if (elapsed >= TRANSITION_SPEED) {
                        activeSlideIndex.value = activeIndex;
                        transitionState.value = "DONE";
                        clipPathCoords.scale = INITIAL_CLIP_PATH_COORDS.scale;
                        cancelAnimationFrame(animationFrameId);

                        return;
                    }
                    animationFrameId = requestAnimationFrame(step);
                };
                animationFrameId = requestAnimationFrame(step);
            });
        });

        const scaledownHandler = $(() => {
            if (transitionState.value !== "TRANSITIONING") {
                animateClipPathScale(0);
                transitionState.value = "DONE";
            }
        });

        const bgVideo =
            backgroundMediaSlide.value?.__typename == "Video" ? (backgroundMediaSlide.value as IGenVideo) : undefined;
        const bgAsset =
            backgroundMediaSlide.value?.__typename == "Asset" ? (backgroundMediaSlide.value as IGenAsset) : undefined;

        return (
            <section
                class={cn(
                    "-mt-[89px]",
                    "relative h-dvh w-full overflow-hidden bg-white",
                    `hero-slider-full-height-${componentIndex}`,
                    transitionState.value === "TRANSITIONING" && "pointer-events-none",
                )}
                ref={heroSliderRef}
                // TEMPORARILY DISABLED FOR NOW AS REQESTED BY LEICHT
                // onMouseMove$={async (e) => {
                //     if (window.innerWidth < BREAKPOINTS.SILVER) return;

                //     const target = e.target as HTMLElement;
                //     if (target.closest(".navigation-btns-container") || target.closest(".card-container")) return;

                //     const heroSlider = heroSliderRef.value;
                //     const { left, width } = heroSlider.getBoundingClientRect();
                //     const currentPosition = (e.clientX - left) / width;

                //     const distanceFromCenter = Math.abs(currentPosition - 0.5);

                //     const scaleMultiplier = 1 + distanceFromCenter;

                //     const baseScale = 5;
                //     const relativeScale = baseScale * scaleMultiplier;

                //     const updateClipPathCoords = () => {
                //         const heroSlider = heroSliderRef.value;
                //         const { left, top } = heroSlider.getBoundingClientRect();
                //         const x = e.clientX - left;
                //         const y = e.clientY - top;
                //         clipPathCoords.x = x;
                //         clipPathCoords.y = y;
                //     };

                //     switch (transitionState.value) {
                //         case "DONE":
                //             updateClipPathCoords();
                //             animateClipPathScale(relativeScale, e);
                //             if (!isAnimating.value) transitionState.value = "IDLE";
                //             break;
                //         case "IDLE":
                //             updateClipPathCoords();
                //             if (!isAnimating.value) animateClipPathScale(relativeScale, e, 0);
                //             if (currentPosition >= 0.5) {
                //                 getNextSlide().then((next) => {
                //                     backgroundImageSlide.value = images![next] as IGenAsset;
                //                 });
                //             } else {
                //                 getPrevSlide().then((prev) => {
                //                     backgroundImageSlide.value = images![prev] as IGenAsset;
                //                 });
                //             }
                //             break;
                //         case "TRANSITIONING":
                //             return;
                //         default:
                //             return;
                //     }
                // }}
                onMouseLeave$={scaledownHandler}
            >
                {topDarkening && (
                    <div
                        class={cn("pointer-events-none absolute inset-0 z-30")}
                        style={{
                            background: "linear-gradient(180deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.00) 28.27%)",
                        }}
                    />
                )}
                {(bgAsset?.src || bgVideo?.videoUrl) && (
                    <>
                        <svg width="0" height="0" class={cn("pointer-events-none absolute inset-0")}>
                            <defs>
                                {/* <style
                                    class="dynamicFluidPathAnimation"
                                    dangerouslySetInnerHTML={dynamicFluidPathAnimation}
                                /> */}
                                <clipPath
                                    id={`blobClip-${componentIndex}`}
                                    style={{
                                        transform: `translate(${clipPathCoords.x}px, ${clipPathCoords.y}px) scale(${clipPathCoords.scale})`,
                                        transitionProperty: "transform",
                                        transitionTimingFunction: "linear",
                                        transformOrigin: "center center",
                                        transitionDuration:
                                            transitionState.value === "TRANSITIONING"
                                                ? `${TRANSITION_SPEED}ms`
                                                : undefined,
                                    }}
                                >
                                    <path d={pathShapes[0]}>
                                        <animate
                                            attributeName="d"
                                            dur="8s"
                                            repeatCount="indefinite"
                                            values={[...pathShapes, pathShapes[0]].join(";")}
                                        />
                                    </path>
                                </clipPath>
                            </defs>
                        </svg>
                        <div
                            class={cn("absolute inset-0 z-[11]")}
                            style={{
                                clipPath: `url(#blobClip-${componentIndex})`,
                                "--webkit-clip-path": `url(#blobClip-${componentIndex})`,
                            }}
                            onClick$={(e) => {
                                if (window.innerWidth < BREAKPOINTS.SILVER) return;

                                const activeIndex = mediaSlides!.indexOf(backgroundMediaSlide.value);
                                handleSlideAnimation(activeIndex, e);
                            }}
                        >
                            {bgVideo && bgVideo.videoUrl ? (
                                <div class="h-full w-full">
                                    <div
                                        class={cn(
                                            "video-container absolute inset-0 z-10",
                                            mediaSlides!.indexOf(backgroundMediaSlide.value) === activeSlideIndex.value
                                                ? "opacity-100"
                                                : "opacity-0",
                                            "hidden", //@TODO for now i do not know how to handle video in the slider background, maybe be do not need it anymore because we diteched the animation, maybe we still need it for static aniamtion dont know yet
                                        )}
                                    >
                                        <EmbededVideo
                                            videoUrl={bgVideo.videoUrl}
                                            id={bgVideo.id!}
                                            autoplay={true}
                                            componentIndex={componentIndex}
                                            hideControls={true}
                                            scaleToFit={true}
                                            ignoreMetaAspectRatio={true}
                                            inSlider={true}
                                            class="h-full w-full"
                                        />
                                    </div>
                                </div>
                            ) : bgAsset ? (
                                <FancyImage
                                    img={bgAsset}
                                    lazyload={componentIndex > 0}
                                    responsiveAspectRatio={{
                                        bronze: 375 / 910,
                                        silver: 843 / 910,
                                        gold: 1280 / 910,
                                        platinum: 1440 / 1024,
                                    }}
                                    responsiveFactor={{
                                        bronze: 1,
                                        silver: 1,
                                        gold: 1,
                                        platinum: 1,
                                    }}
                                    class={cn(
                                        "pointer-events-none h-full w-full",
                                        transitionState.value === "IDLE"
                                            ? "silver:scale-110"
                                            : "silver:scale-100 silver:transition-transform silver:delay-[50ms] silver:duration-[950ms] silver:ease-[cubic-bezier(0.215,0.61,0.355,1)]",
                                    )}
                                />
                            ) : null}
                        </div>
                    </>
                )}
                <HeroSliderFullHeightInner
                    activeSlideIndex={activeSlideIndex}
                    backgroundImageSlide={backgroundMediaSlide}
                    componentIndex={componentIndex}
                    getNextSlide={getNextSlide}
                    getPrevSlide={getPrevSlide}
                    handleSlideAnimation={handleSlideAnimation}
                    mediaSlides={mediaSlides}
                    scaledownHandler={scaledownHandler}
                    transitionState={transitionState}
                    clipPathCoords={clipPathCoords}
                    {...props}
                />
            </section>
        );
    },
);
